import React, { useCallback, useMemo } from 'react';
import { flatten, uniq } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { getAccessGroupColumnSort, AccessGroup, getAccessGroupFilters, AccessGroupTableName, getAccessGroupDocumentGroups, getIsFetchingAccessGroupDocumentGroups } from './store';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { useAppSelector } from '../../../hooks/react-redux';
import { Toggle } from '../../shared/toggle';
import { AccessGroupTableHeader } from './AccessGroupTableHeader';
import { LoadingDots } from '../../shared/loading-dots/LoadingDots';

interface AccessGroupDocumentGroupsTableProps {
    documentNameIds: number[];
    updateGroup: (key: keyof AccessGroup, ids: number[]) => void;
    scrollableHeight: number;
}

export const AccessGroupDocumentGroupsTable: React.FC<AccessGroupDocumentGroupsTableProps> = ({ documentNameIds, updateGroup, scrollableHeight }) => {
    const columnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT_GROUP));
    const filters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.DOCUMENT_GROUP));
    const documentGroups = useAppSelector(getAccessGroupDocumentGroups);
    const isFetchingDocumentGroups = useAppSelector(getIsFetchingAccessGroupDocumentGroups);

    const allDocumentNameIdsFromGroups = flatten(documentGroups.map(({ documentNameIds }) => documentNameIds));
    const allSelected = useMemo(() => documentGroups.length > 0 && allDocumentNameIdsFromGroups.every(documentNameId => documentNameIds.includes(documentNameId)), [documentGroups, documentNameIds, allDocumentNameIdsFromGroups]);

    const toggleSelectAll = useCallback(() => {
        const updatedDocumentNameIds = allSelected ? documentNameIds.filter(id => !allDocumentNameIdsFromGroups.includes(id)) : allDocumentNameIdsFromGroups;
        updateGroup('documentNameIds', updatedDocumentNameIds);
    }, [allSelected, updateGroup, allDocumentNameIdsFromGroups, documentNameIds]);

    const groupDocumentInGroup = useCallback((groupDocumentNameIds: number[]) => groupDocumentNameIds.every(entityId => documentNameIds.includes(entityId)), [documentNameIds]);

    const toggleGroupDocumentInAccessGroup = useCallback((value: boolean, groupEntities: number[]) => {
        const updatedDocumentNameIds = !value ? documentNameIds.filter(id => !groupEntities.includes(id)) : uniq([...documentNameIds, ...groupEntities]);
        updateGroup('documentNameIds', updatedDocumentNameIds);
    }, [documentNameIds, updateGroup]);

    const tableHeaders = [
        { width: '50%', title: 'Group Name', column: 'groupName' },
        { width: '30%', title: 'Group Description', column: 'groupDescription' },
        { width: '20%', title: 'Included', column: 'groupIncluded' }
    ];

    return (
        <div className={styles.accessGroupContentWrapper}>
            <AccessGroupTableHeader
                allSelected={allSelected}
                toggleSelectAll={toggleSelectAll}
                textFilterColumns={['groupName', 'groupDescription']}
                tableHeaders={tableHeaders}
                selectAllLabel='Documents'
                filters={filters}
                columnSort={columnSort}
                tableName={AccessGroupTableName.DOCUMENT_GROUP}
            />
            <Scrollable height={`${scrollableHeight}px`}>
                {isFetchingDocumentGroups ? <div className={styles.tableLoadingDots} style={{ height: scrollableHeight }}><LoadingDots /></div> :
                    documentGroups.map(({ clientDocumentGroupId, groupName, description, documentNameIds }) => {
                        const inGroup = groupDocumentInGroup(documentNameIds);
                        return (
                            <div key={clientDocumentGroupId} className={styles.tableRow}>
                                <div className={styles.tableCell} style={{ width: '40%' }}>{groupName}</div>
                                <div className={styles.tableCell} style={{ width: '40%' }}>{description}</div>
                                <div className={styles.tableCell} style={{ width: '20%' }}>
                                    <Toggle
                                        onChange={value => toggleGroupDocumentInAccessGroup(value, documentNameIds)}
                                        checked={inGroup}
                                    />
                                </div>
                            </div>
                        );
                    })
                }
            </Scrollable>
        </div>
    );
};
